import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileSpreadsheet, Download, Users, GraduationCap, DollarSign, Calendar, Loader2, FileText, ClipboardList, Printer, RefreshCw, Save } from 'lucide-react';
import { downloadReport } from './AcademicoRelatorios/reporting/ExportActions';

export default function Relatorios() {
  const [loading, setLoading] = useState(false);
  const [tipoRelatorio, setTipoRelatorio] = useState('alunos');
  const [turmas, setTurmas] = useState([]);
  const [turmaSelecionada, setTurmaSelecionada] = useState('todas');
  const [disciplinas, setDisciplinas] = useState([]);
  const [disciplinaSelecionada, setDisciplinaSelecionada] = useState('todas');
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear().toString());
  
  // Estados para Lista de Chamada
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [rosterData, setRosterData] = useState(null);
  const [editingCallNumber, setEditingCallNumber] = useState(null);
  const [newCallNumber, setNewCallNumber] = useState('');
  const printRef = useRef(null);
  
  // Campos selecionáveis por tipo
  const camposDisponiveis = {
    alunos: [
      { id: 'name', label: 'Nome', default: true },
      { id: 'registration_number', label: 'Matrícula', default: true },
      { id: 'class_name', label: 'Turma', default: true },
      { id: 'birth_date', label: 'Data Nascimento', default: false },
      { id: 'cpf', label: 'CPF', default: false },
      { id: 'rg', label: 'RG', default: false },
      { id: 'address', label: 'Endereço', default: false },
      { id: 'city', label: 'Cidade', default: false },
      { id: 'phone', label: 'Telefone', default: false },
      { id: 'email', label: 'Email', default: false },
      { id: 'responsible_name', label: 'Responsável', default: true },
      { id: 'responsible_phone', label: 'Tel. Responsável', default: true },
    ],
    lista_chamada: [
      { id: 'call_number', label: 'Nº Chamada', default: true },
      { id: 'name', label: 'Nome Completo', default: true },
      { id: 'class_name', label: 'Turma', default: true },
      { id: 'birth_date', label: 'Data Nascimento', default: true },
    ],
    equipe: [
      { id: 'full_name', label: 'Nome Completo', default: true },
      { id: 'email', label: 'Email', default: true },
      { id: 'role', label: 'Cargo', default: true },
      { id: 'cpf', label: 'CPF', default: false },
      { id: 'rg', label: 'RG', default: false },
      { id: 'phone', label: 'Telefone', default: false },
      { id: 'cellphone', label: 'Celular', default: false },
      { id: 'address', label: 'Endereço', default: false },
      { id: 'bank_name', label: 'Banco', default: false },
      { id: 'bank_agency', label: 'Agência', default: false },
      { id: 'bank_account', label: 'Conta', default: false },
      { id: 'pix_key', label: 'PIX', default: false },
      { id: 'admission_date', label: 'Data Admissão', default: false },
    ],
    financeiro: [
      { id: 'student_name', label: 'Aluno', default: true },
      { id: 'responsible_name', label: 'Responsável', default: true },
      { id: 'description', label: 'Descrição', default: true },
      { id: 'amount', label: 'Valor', default: true },
      { id: 'due_date', label: 'Vencimento', default: true },
      { id: 'status', label: 'Status', default: true },
      { id: 'payment_date', label: 'Data Pagamento', default: false },
      { id: 'payment_method', label: 'Forma Pagamento', default: false },
    ],
    reunioes: [
      { id: 'titulo', label: 'Título', default: true },
      { id: 'data', label: 'Data', default: true },
      { id: 'hora_inicio', label: 'Hora', default: true },
      { id: 'student_name', label: 'Aluno', default: true },
      { id: 'responsavel_name', label: 'Responsável', default: true },
      { id: 'local', label: 'Local', default: true },
      { id: 'status', label: 'Status', default: true },
      { id: 'assunto', label: 'Assunto', default: false },
    ],
    notas: [
      { id: 'student_name', label: 'Aluno', default: true },
      { id: 'class_name', label: 'Turma', default: true },
      { id: 'subject_name', label: 'Disciplina', default: true },
      { id: 'av1', label: 'AV1', default: true },
      { id: 'av2', label: 'AV2', default: true },
      { id: 'av3', label: 'AV3', default: true },
      { id: 'av4', label: 'AV4', default: true },
      { id: 'media', label: 'Média', default: true },
      { id: 'recuperacao', label: 'Recuperação', default: true },
      { id: 'media_final', label: 'Média Final', default: true },
      { id: 'faltas', label: 'Faltas', default: true },
      { id: 'status', label: 'Status', default: true },
    ],
  };

  const [camposSelecionados, setCamposSelecionados] = useState(
    camposDisponiveis.alunos.filter(c => c.default).map(c => c.id)
  );

  useEffect(() => {
    loadTurmas();
    loadDisciplinas();
  }, []);

  useEffect(() => {
    loadTurmas();
  }, [anoSelecionado]);

  useEffect(() => {
    setCamposSelecionados(
      camposDisponiveis[tipoRelatorio]?.filter(c => c.default).map(c => c.id) || []
    );
  }, [tipoRelatorio]);

  const loadTurmas = async () => {
    try {
      const res = await api.get('/class-groups');
      // Filtrar por ano se selecionado
      const filtered = anoSelecionado !== 'todos' 
        ? res.data.filter(t => t.name?.includes(anoSelecionado) || t.school_year?.includes(anoSelecionado))
        : res.data;
      setTurmas(filtered);
    } catch (e) {}
  };

  const loadDisciplinas = async () => {
    try {
      const res = await api.get('/curriculum-subjects');
      setDisciplinas(res.data);
    } catch (e) {}
  };

  const toggleCampo = (campoId) => {
    setCamposSelecionados(prev => 
      prev.includes(campoId) 
        ? prev.filter(c => c !== campoId)
        : [...prev, campoId]
    );
  };

  const selecionarTodos = () => {
    setCamposSelecionados(camposDisponiveis[tipoRelatorio].map(c => c.id));
  };

  const limparSelecao = () => {
    setCamposSelecionados([]);
  };

  const gerarRelatorio = async (formato = 'xlsx') => {
    if (camposSelecionados.length === 0) {
      toast.error('Selecione pelo menos um campo');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/relatorios/gerar', {
        tipo: tipoRelatorio,
        campos: camposSelecionados,
        turma_id: turmaSelecionada !== 'todas' ? turmaSelecionada : null,
        disciplina_id: disciplinaSelecionada !== 'todas' ? disciplinaSelecionada : null,
        ano: anoSelecionado !== 'todos' ? anoSelecionado : null,
        formato: formato
      }, { responseType: 'blob' });

      // Criar blob e fazer download
      const blob = new Blob([response.data], { 
        type: formato === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${tipoRelatorio}_${new Date().toISOString().split('T')[0]}.${formato}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Relatório ${formato.toUpperCase()} gerado!`);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  const tiposRelatorio = [
    { id: 'alunos', label: 'Alunos', icon: Users },
    { id: 'lista_chamada', label: 'Lista de Chamada', icon: ClipboardList },
    { id: 'equipe', label: 'Equipe DNA', icon: GraduationCap },
    { id: 'financeiro', label: 'Financeiro', icon: DollarSign },
    { id: 'reunioes', label: 'Reuniões', icon: Calendar },
    { id: 'notas', label: 'Resumo de Notas', icon: FileSpreadsheet },
  ];

  // Funções para Lista de Chamada
  const loadRoster = async () => {
    if (turmaSelecionada === 'todas') {
      toast.error('Selecione uma turma específica');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/reports/class-roster/${turmaSelecionada}`);
      setRosterData(res.data);
      setShowRosterDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar lista de chamada');
    } finally {
      setLoading(false);
    }
  };

  const generateCallNumbers = async () => {
    if (turmaSelecionada === 'todas') {
      toast.error('Selecione uma turma específica');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post(`/students/generate-call-numbers/${turmaSelecionada}`);
      toast.success(res.data.message);
      // Recarregar lista
      loadRoster();
    } catch (error) {
      toast.error('Erro ao gerar números de chamada');
    } finally {
      setLoading(false);
    }
  };

  const updateCallNumber = async (studentId, callNumber) => {
    try {
      await api.put(`/students/${studentId}/call-number`, null, {
        params: { call_number: callNumber }
      });
      toast.success('Número atualizado');
      setEditingCallNumber(null);
      // Atualizar localmente
      if (rosterData) {
        setRosterData({
          ...rosterData,
          students: rosterData.students.map(s => 
            s.student_id === studentId ? { ...s, call_number: callNumber } : s
          ).sort((a, b) => (a.call_number || 999) - (b.call_number || 999))
        });
      }
    } catch (error) {
      toast.error('Erro ao atualizar número');
    }
  };

  const printRoster = async () => {
    if (!rosterData) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Não foi possível abrir a impressão.'); return; }
    try {
      await downloadReport('lista-chamada', 'pdf', { turma_ids: rosterData.class_group?.id || turmaSelecionada, ano_letivo: rosterData.class_group?.year || anoSelecionado, colunas: 'n,name,birth_date', modo: 'personalizada', ordenacao: 'numero_chamada' }, printWindow);
    } catch (error) { toast.error(error.message); }
  };

  const anos = ['2024', '2025', '2026', '2027', '2028'];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-green-600" />
          Relatórios
        </h1>
        <p className="text-muted-foreground">Exporte dados do sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tipo de Relatório */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">1. Tipo de Relatório</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tiposRelatorio.map(tipo => (
              <div
                key={tipo.id}
                onClick={() => setTipoRelatorio(tipo.id)}
                className={`p-3 border rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${
                  tipoRelatorio === tipo.id ? 'border-green-500 bg-green-50' : 'hover:bg-muted'
                }`}
              >
                <tipo.icon className={`w-5 h-5 ${tipoRelatorio === tipo.id ? 'text-green-600' : 'text-muted-foreground'}`} />
                <span className={tipoRelatorio === tipo.id ? 'font-medium' : ''}>{tipo.label}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Campos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex justify-between items-center">
              2. Campos
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selecionarTodos}>Todos</Button>
                <Button variant="ghost" size="sm" onClick={limparSelecao}>Limpar</Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {camposDisponiveis[tipoRelatorio]?.map(campo => (
                <div key={campo.id} className="flex items-center gap-2">
                  <Checkbox
                    id={campo.id}
                    checked={camposSelecionados.includes(campo.id)}
                    onCheckedChange={() => toggleCampo(campo.id)}
                  />
                  <label htmlFor={campo.id} className="text-sm cursor-pointer">{campo.label}</label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Filtros e Gerar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">3. Filtros e Exportar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Ano */}
            {(tipoRelatorio === 'alunos' || tipoRelatorio === 'notas' || tipoRelatorio === 'financeiro') && (
              <div>
                <label className="text-sm font-medium">Ano Letivo</label>
                <Select value={anoSelecionado} onValueChange={setAnoSelecionado}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os anos</SelectItem>
                    {anos.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Turma */}
            {(tipoRelatorio === 'alunos' || tipoRelatorio === 'financeiro' || tipoRelatorio === 'notas' || tipoRelatorio === 'lista_chamada') && (
              <div>
                <label className="text-sm font-medium">Turma</label>
                <Select value={turmaSelecionada} onValueChange={setTurmaSelecionada}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as turmas" />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoRelatorio !== 'lista_chamada' && (
                      <SelectItem value="todas">Todas as turmas</SelectItem>
                    )}
                    {turmas.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Disciplina */}
            {tipoRelatorio === 'notas' && (
              <div>
                <label className="text-sm font-medium">Disciplina</label>
                <Select value={disciplinaSelecionada} onValueChange={setDisciplinaSelecionada}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as disciplinas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas as disciplinas</SelectItem>
                    {disciplinas.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                {camposSelecionados.length} campo(s) selecionado(s)
              </p>
            </div>

            {/* Botões específicos para Lista de Chamada */}
            {tipoRelatorio === 'lista_chamada' ? (
              <>
                {/* Botão de Gerar Números - Destaque */}
                <Button 
                  onClick={generateCallNumbers} 
                  disabled={loading || turmaSelecionada === 'todas'}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                  data-testid="generate-call-numbers-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Gerar Números de Chamada (A-Z)
                </Button>
                
                <Button 
                  onClick={loadRoster} 
                  disabled={loading || turmaSelecionada === 'todas'}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando...</>
                  ) : (
                    <><ClipboardList className="w-4 h-4 mr-2" /> Visualizar Lista de Chamada</>
                  )}
                </Button>
                
                {turmaSelecionada === 'todas' && (
                  <p className="text-xs text-amber-600 text-center">
                    Selecione uma turma específica
                  </p>
                )}
              </>
            ) : (
              <>
                <Button 
                  onClick={() => gerarRelatorio('xlsx')} 
                  disabled={loading || camposSelecionados.length === 0}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...</>
                  ) : (
                    <><FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel</>
                  )}
                </Button>
                
                <Button 
                  onClick={() => gerarRelatorio('pdf')} 
                  disabled={loading || camposSelecionados.length === 0}
                  variant="outline"
                  className="w-full"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...</>
                  ) : (
                    <><FileText className="w-4 h-4 mr-2" /> Exportar PDF</>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Lista de Chamada */}
      <Dialog open={showRosterDialog} onOpenChange={setShowRosterDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Lista de Chamada - {rosterData?.class_group?.name || ''}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              <span data-testid="legacy-roster-items-loaded">{rosterData?.students?.length || 0} itens carregados</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={generateCallNumbers}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Renumerar A-Z
              </Button>
              <Button size="sm" onClick={printRoster}>
                <Printer className="w-4 h-4 mr-1" />
                Imprimir
              </Button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto border rounded-lg">
            <Table ref={printRef}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 text-center">Nº</TableHead>
                  <TableHead>Nome Completo</TableHead>
                  <TableHead className="w-40">Turma</TableHead>
                  <TableHead className="w-40">Data Nascimento</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rosterData?.students?.map((student, idx) => (
                  <TableRow key={student.student_id}>
                    <TableCell className="text-center">
                      {editingCallNumber === student.student_id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={newCallNumber}
                            onChange={(e) => setNewCallNumber(e.target.value)}
                            className="w-16 h-7 text-center"
                            min="1"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateCallNumber(student.student_id, parseInt(newCallNumber));
                              } else if (e.key === 'Escape') {
                                setEditingCallNumber(null);
                              }
                            }}
                          />
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-7 w-7"
                            onClick={() => updateCallNumber(student.student_id, parseInt(newCallNumber))}
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <span 
                          className="cursor-pointer hover:bg-blue-100 px-2 py-1 rounded font-bold"
                          onClick={() => {
                            setEditingCallNumber(student.student_id);
                            setNewCallNumber(student.call_number?.toString() || '');
                          }}
                          title="Clique para editar"
                        >
                          {student.call_number || '-'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{student.name}</TableCell>
                    <TableCell>{student.class_name}</TableCell>
                    <TableCell>
                      {student.birth_date ? (() => { const m = String(student.birth_date).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : '-'; })() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRosterDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
